/**
 * "Live rollout" — how many organizations have the feature, and by what route
 * (LOCKER-408, design canvas *Proposed*).
 *
 * The stacked bar splits granted access by SOURCE rather than showing one
 * on/off ratio: "24 of 406 organizations" answers a different question from
 * "24 explicitly, 0 by default", and the second is the one that tells an
 * operator whether flipping the feature default would move anything.
 */
import { Box, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { FeatureRolloutCounts } from "./featureRolloutModel"

const ASSIGNED_COLOR = "#234285"
const DEFAULT_COLOR = "#94D2C2"
const OFF_COLOR = "#cfd8dc"

const LegendSwatch: FC<{ color: string; label: string; count: number }> = ({
  color,
  label,
  count,
}) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box
      sx={{
        width: 9,
        height: 9,
        borderRadius: 0.5,
        bgcolor: color,
        flex: "0 0 auto",
      }}
    />
    <Typography variant="caption" color="text.secondary">
      {label} — {count}
    </Typography>
  </Stack>
)

export const FeatureRolloutSummary: FC<{ counts: FeatureRolloutCounts }> = ({
  counts,
}) => {
  const { t } = useTranslation()
  const { total } = counts
  const notGranted = total - counts.granted

  // Guard the divide: a tenants query that returned nothing must render an
  // empty bar, not `NaN%` widths that collapse the whole card.
  const pct = (n: number) => (total > 0 ? `${(n / total) * 100}%` : "0%")

  return (
    <Stack spacing={1.5}>
      <Typography
        variant="caption"
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontWeight: 600,
          color: "text.secondary",
        }}
      >
        {t("feature.liveRollout")}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ color: ASSIGNED_COLOR, lineHeight: 1 }}
          data-testid="feature-rollout-granted-count"
        >
          {counts.granted}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("feature.ofOrganizationsHaveFeature", { count: total })}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        sx={{
          height: 10,
          borderRadius: 5,
          overflow: "hidden",
          bgcolor: "#eceff1",
        }}
      >
        <Box
          sx={{
            width: pct(counts.activatedByAssignment),
            bgcolor: ASSIGNED_COLOR,
          }}
        />
        <Box
          sx={{ width: pct(counts.activatedByDefault), bgcolor: DEFAULT_COLOR }}
        />
        <Box sx={{ width: pct(notGranted), bgcolor: OFF_COLOR }} />
      </Stack>

      <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap>
        <LegendSwatch
          color={ASSIGNED_COLOR}
          label={t("feature.legendOnSetHere")}
          count={counts.activatedByAssignment}
        />
        <LegendSwatch
          color={DEFAULT_COLOR}
          label={t("feature.legendOnViaDefault")}
          count={counts.activatedByDefault}
        />
        <LegendSwatch
          color={OFF_COLOR}
          label={t("feature.legendOff")}
          count={notGranted}
        />
      </Stack>
    </Stack>
  )
}

export default FeatureRolloutSummary
